/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Link } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { QUESTION_IDS_TYPE } from 'bundles/onboarding-common/constants/questionScheme';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { usePageConfigComponentMetadata } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';

import _t from 'i18n!nls/unified-common';

type Props = {
  title: JSX.Element;
  trackingLinkName: string;
  onboardingText?: string;
  onboardingQuestion?: QUESTION_IDS_TYPE;
  hideHeaderLink?: boolean;
  shouldShowOnboardingV3?: boolean;
};

const styles = {
  container: css`
    &,
    h2 {
      display: inline;
    }
  `,
  link: css`
    display: inline-block;
    margin-inline-start: var(--cds-spacing-100);
  `,
};

/**
 *
 * @description This component is the header title for the recommended role card to redirect the user to the onboarding page
 */
const RecommendedRoleHeader = ({
  title,
  trackingLinkName: linkName,
  onboardingText,
  onboardingQuestion,
  hideHeaderLink,
  shouldShowOnboardingV3,
}: Props) => {
  const trackV3 = useTracker();
  const defaultCtaText = shouldShowOnboardingV3 ? _t('Edit your interests') : _t('Edit goal');
  const defaultQuestionId = shouldShowOnboardingV3 ? QUESTION_IDS.roleSelect : QUESTION_IDS.learningGoal;

  const isOnboardingV3TextIncludesGoal = shouldShowOnboardingV3 && onboardingText?.includes('goal');
  const overrideOnboardingText = onboardingText && !isOnboardingV3TextIncludesGoal ? onboardingText : defaultCtaText;

  const isOnboardingV3QuestionIdIncludesGoal = shouldShowOnboardingV3 && onboardingQuestion?.includes('goal');
  const { onboardingUrl } = useBuildOnboardingUrl({
    questionId: isOnboardingV3QuestionIdIncludesGoal
      ? QUESTION_IDS.roleSelect
      : onboardingQuestion ?? defaultQuestionId,
  });

  const pageConfigComponentMetadata = usePageConfigComponentMetadata();
  const pageConfigComponentTrackingData = {
    id: pageConfigComponentMetadata?.id,
    index: pageConfigComponentMetadata?.index ?? -1,
    name: pageConfigComponentMetadata?.name ?? 'unknown',
    recommender: pageConfigComponentMetadata?.recommender,
  };

  return (
    <div css={styles.container}>
      {title}
      {!hideHeaderLink && (
        <Link
          variant="quiet"
          typographyVariant="subtitleMedium"
          css={styles.link}
          href={onboardingUrl}
          onClick={() => {
            trackV3('click_button', {
              button: {
                linkURL: onboardingUrl,
                linkName,
                name: 'onboarding_edit',
                pageConfigComponent: pageConfigComponentTrackingData,
              },
            });
          }}
          data-testid="edit-goal-link"
        >
          {overrideOnboardingText}
        </Link>
      )}
    </div>
  );
};

export default RecommendedRoleHeader;
